import React from "react";
import GeoIcon, { BloxColor } from "../icons/GeoIcon.jsx";
import "./blox-shared.css";

/**
 * BloxEmptyState — the canonical empty-state pattern used wherever
 * a list / panel has nothing to show.
 *
 * Visual: large GeoIcon at reduced opacity (the brand voice says
 * "intentional emptiness, not absence"), title, supporting copy, and
 * optionally a single primary CTA that points to the next action.
 *
 * Props:
 *   shape    — GeoIcon shape name (e.g. "vault", "ledger", "network")
 *   color    — BloxColor token, default green
 *   title    — primary heading
 *   message  — supporting line(s)
 *   cta      — optional { label, onClick }
 *   size     — "compact" | "default" | "hero". Default "default".
 *
 * The common-language win: every empty state reads at a glance because
 * the icon vocabulary is the proprietary set, the typography ladder is
 * fixed, and the CTA placement is consistent.
 */
export default function BloxEmptyState({
  shape = "vault",
  color = BloxColor.green,
  title,
  message,
  cta,
  size = "default",
  className = "",
}) {
  const iconSize = size === "compact" ? 40 : size === "hero" ? 80 : 56;

  return (
    <div className={`blox-empty-state blox-empty-state--${size} ${className}`}>
      <div className="blox-empty-icon">
        <GeoIcon shape={shape} color={color} size={iconSize} className="" />
      </div>
      {title && <div className="blox-empty-title">{title}</div>}
      {message && <div className="blox-empty-message">{message}</div>}
      {cta && (
        <button
          type="button"
          className="blox-empty-cta"
          onClick={cta.onClick}
        >
          {cta.label}
        </button>
      )}
    </div>
  );
}
